import {
  AvailableAssetLocationDto,
  PreferenceCardResponseDto,
  PreferenceCardTrackedAssetsDto,
  PreferenceCardUntrackedAssetDto,
  PreferenceCardUntrackedAssetsDto,
  TrackedAssetCatalogItemDto,
} from '@peritasai/types';

/**
 * Represents a storage location where tracked assets are available
 */
export class AvailableAssetLocationEntity {
  public readonly id: string | null;
  public readonly name: string;
  public readonly count: number;

  constructor(dto: AvailableAssetLocationDto) {
    this.id = dto.locationId;
    this.name = dto.locationName;
    this.count = dto.count;
  }
}

/**
 * Represents a tracked catalog item row within a preference card
 */
export class TrackedAssetCatalogItemEntity {
  public readonly catalogItemId: string;
  public readonly type: TrackedAssetCatalogItemDto['type'];
  public readonly name: string;
  public readonly manufacturer: string;
  public readonly productNumber: string;
  public readonly description?: string;
  public readonly quantity: number;
  public readonly note?: string;
  public readonly metadata?: Record<string, unknown> | null;
  public readonly availableCount: number;
  public readonly availableLocations: AvailableAssetLocationEntity[];

  constructor(dto: TrackedAssetCatalogItemDto) {
    this.catalogItemId = dto.catalogItemId;
    this.type = dto.type;
    this.name = dto.name;
    this.manufacturer = dto.manufacturer;
    this.productNumber = dto.productNumber;
    this.description = dto.description;
    this.quantity = dto.quantity;
    this.note = dto.note;
    this.metadata = dto.metadata;
    this.availableCount = dto.availableCount;
    this.availableLocations = dto.availableAssetLocations.map(
      (location) => new AvailableAssetLocationEntity(location)
    );
  }

  /**
   * Indicates whether the catalog item currently has any availability
   */
  public hasAvailability(): boolean {
    return this.availableCount > 0;
  }
}

/**
 * Represents the tracked assets grouped by asset type
 */
export class PreferenceCardTrackedAssetsEntity {
  public readonly caseCarts: TrackedAssetCatalogItemEntity[];
  public readonly trays: TrackedAssetCatalogItemEntity[];
  public readonly equipment: TrackedAssetCatalogItemEntity[];
  public readonly implants: TrackedAssetCatalogItemEntity[];
  public readonly stretchers: TrackedAssetCatalogItemEntity[];

  constructor(dto: PreferenceCardTrackedAssetsDto) {
    this.caseCarts = dto.caseCarts.map(
      (item) => new TrackedAssetCatalogItemEntity(item)
    );
    this.trays = dto.trays.map((item) => new TrackedAssetCatalogItemEntity(item));
    this.equipment = dto.equipment.map(
      (item) => new TrackedAssetCatalogItemEntity(item)
    );
    this.implants = dto.implants.map(
      (item) => new TrackedAssetCatalogItemEntity(item)
    );
    this.stretchers = dto.stretchers.map(
      (item) => new TrackedAssetCatalogItemEntity(item)
    );
  }

  /**
   * Returns all tracked assets regardless of type
   */
  public getAllTrackedAssets(): TrackedAssetCatalogItemEntity[] {
    return [
      ...this.caseCarts,
      ...this.trays,
      ...this.equipment,
      ...this.implants,
      ...this.stretchers,
    ];
  }
}

/**
 * Represents an untracked preference card asset row
 */
export class PreferenceCardUntrackedAssetEntity {
  public readonly id: string;
  public readonly catalogId: string;
  public readonly type: PreferenceCardUntrackedAssetDto['type'];
  public readonly name: string;
  public readonly productNumber: string;
  public readonly manufacturer: string;
  public readonly description?: string;
  public readonly cost?: number;
  public readonly quantity: number;
  public readonly note?: string;
  public readonly metadata?: Record<string, unknown> | null;

  constructor(dto: PreferenceCardUntrackedAssetDto) {
    this.id = dto.id;
    this.catalogId = dto.untrackedAssetCatalogId;
    this.type = dto.type;
    this.name = dto.name;
    this.productNumber = dto.productNumber;
    this.manufacturer = dto.manufacturer;
    this.description = dto.description;
    this.cost = dto.cost;
    this.quantity = dto.quantity;
    this.note = dto.note;
    this.metadata = dto.metadata;
  }
}

/**
 * Represents grouped untracked assets for a preference card
 */
export class PreferenceCardUntrackedAssetsEntity {
  public readonly supplies: PreferenceCardUntrackedAssetEntity[];
  public readonly singlePackedInstruments: PreferenceCardUntrackedAssetEntity[];
  public readonly instruments: PreferenceCardUntrackedAssetEntity[];

  constructor(dto: PreferenceCardUntrackedAssetsDto) {
    this.supplies = dto.supplies.map(
      (item) => new PreferenceCardUntrackedAssetEntity(item)
    );
    this.singlePackedInstruments = dto.singlePackedInstruments.map(
      (item) => new PreferenceCardUntrackedAssetEntity(item)
    );
    this.instruments = (dto.instruments ?? []).map(
      (item) => new PreferenceCardUntrackedAssetEntity(item)
    );
  }

  /**
   * Returns all untracked assets regardless of grouping
   */
  public getAllUntrackedAssets(): PreferenceCardUntrackedAssetEntity[] {
    return [
      ...this.supplies,
      ...this.singlePackedInstruments,
      ...this.instruments,
    ];
  }
}

/**
 * Preference card aggregate entity
 */
export class PreferenceCardEntity {
  public readonly id: string;
  public readonly hospitalId: string;
  public readonly surgeonId: string;
  public readonly caseType: PreferenceCardResponseDto['caseType'];
  public readonly version: number;
  public readonly isActive: boolean;
  public readonly trackedAssets: PreferenceCardTrackedAssetsEntity;
  public readonly untrackedAssets: PreferenceCardUntrackedAssetsEntity;

  private readonly raw: PreferenceCardResponseDto;

  constructor(dto: PreferenceCardResponseDto) {
    this.raw = dto;
    this.id = dto.preferenceCardId;
    this.hospitalId = dto.hospitalPreferenceCardId;
    this.surgeonId = dto.surgeon.id;
    this.caseType = dto.caseType;
    this.version = dto.version;
    this.isActive = dto.isActive;
    this.trackedAssets = new PreferenceCardTrackedAssetsEntity(dto.trackedAssets);
    this.untrackedAssets = new PreferenceCardUntrackedAssetsEntity(
      dto.untrackedAssets
    );
  }

  /**
   * Indicates whether any tracked assets have been defined for this preference card
   */
  public hasTrackedAssets(): boolean {
    return this.trackedAssets.getAllTrackedAssets().length > 0;
  }

  /**
   * Indicates whether any untracked assets have been defined for this preference card
   */
  public hasUntrackedAssets(): boolean {
    return this.untrackedAssets.getAllUntrackedAssets().length > 0;
  }

  /**
   * Returns the original DTO representation (useful for serialization)
   */
  public toDto(): PreferenceCardResponseDto {
    return this.raw;
  }
}

